import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Calendar,
  Users,
  Euro,
  Clock,
  MapPin
} from "lucide-react";
import { cn, formatBudget, formatTravelDates, formatCreatedDate, calculateDuration } from "@/components/utils/utils";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequestBadge } from "./TravelRequestBadge";
import { TravelRequestButton } from "./TravelRequestButton";
import { useI18n } from '@/hooks/useI18n';

interface TravelRequestCardProps {
  travelRequest: DashboardTravelRequest;
  userRole: "travel_designer" | "travel_advisor_member" | "admin" | "traveler";
  className?: string;
  onViewRequest: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
}

export const TravelRequestCard = ({ travelRequest, userRole, className, onViewRequest, onCreateItinerary }: TravelRequestCardProps) => {
  const { t } = useI18n();
  
  return (
    <Card
      className={cn(
        "border border-gray-200 rounded-2xl transition-all hover:border-gray-300",
        className,
      )}
    >
      <CardContent className="p-3 h-full flex flex-col">
        {/* Header con data e badge */}
        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-600">
            {formatCreatedDate(travelRequest.createdAt)}
          </span>
          <div className="flex justify-end flex-wrap gap-1">
            <TravelRequestBadge travelRequest={travelRequest} variant="card" />
          </div>
        </div>

        {/* Titolo del viaggio */}
        <h4 className="text-black mb-4 line-clamp-1">
          {travelRequest.destinations.length > 0
            ? travelRequest.destinations.join(", ")
            : "Destinazione da definire"}
        </h4>

        {/* Dettagli del viaggio */}
        <div className="text-sm flex-1 py-3">
          <div className="grid grid-cols-2 gap-4">
            {/* Date */}
            <div className="flex items-center gap-2 col-span-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">
                {formatTravelDates(
                  travelRequest.travelStartDate,
                  travelRequest.travelEndDate,
                )}
              </span>
            </div>
            {/* Prima colonna */}
            <div className="space-y-3">
              {/* People */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {travelRequest.totalGuests || "N/A"}
                </span>
              </div>

              {/* Budget */}
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {formatBudget(
                    travelRequest.budgetMin,
                    travelRequest.budgetMax,
                    { showCurrency: false },
                  )}
                </span>
              </div>
            </div>

            {/* Seconda colonna */}
            <div className="space-y-3">
              {/* Duration */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {calculateDuration(
                    travelRequest.travelStartDate,
                    travelRequest.travelEndDate,
                    { t },
                  )}
                </span>
              </div>

              {/* Departure */}
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600 line-clamp-1">
                  {travelRequest.departurePoint ||
                    t("travelRequest.departureTBD")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer con contatto e bottone */}
        <div className="mt-4 space-y-3">
          {/* Contact info */}
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarFallback
                className={`text-sm text-white ${getAvatarColor(travelRequest.contactEmail)}`}
              >
                {getInitials(
                  travelRequest.contact.firstName,
                  travelRequest.contact.lastName,
                )}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-gray-900 font-medium">
              {travelRequest.contactFullName}
            </span>
          </div>

          {/* Main action button */}
          <TravelRequestButton
            travelRequest={travelRequest}
            onClick={onViewRequest}
            onCreateItinerary={onCreateItinerary}
            variant="card"
            userRole={userRole}
          />
        </div>
      </CardContent>
    </Card>
  );
};
